import React from "react";
import { useContext, useEffect, useState } from "react";
import { Button, Col, Form, Row, Card, Alert } from "react-bootstrap";

import PortalService from '../../../services/portal.service';
import StateContext from '../../../context/State';
import AddCustomFieldModal from "./AddCustomFieldModal";
import AddIntakeTemplatesModal from "./AddIntakeTemplatesModal";

import type { CustomField } from "../../../types";
import { readPerClientValue } from '../../../../../../../share/utils/perClientCustomFields';
import { contactIdentityOf } from '../../../../../../../share/utils/contactIdentity';

type CustomFieldsProps = {
    displayMode?: string;
    editMode?: boolean;
    // The contact whose per-client values the simple view shows. They are read under the
    // key fixed for that contact at page load, not under the name being typed.
    contact?: unknown;
};

const CustomFields = ({ displayMode = "full", editMode = false, contact }: CustomFieldsProps) => {
    const { state } = useContext(StateContext);
    const portalService = new PortalService(state);
    const [showIntakeTemplatesModal, setShowIntakeTemplatesModal] = useState<boolean>(false);
    const [showIndividualFieldsModal, setShowIndividualFieldsModal] = useState<boolean>(false);
    const [customFields, setCustomFields] = useState<any>([]);

    useEffect(() => {
        // adding delay to account for potential empty array
        const timeout = setTimeout(() => {
            getCustomFieldsData();
        }, 2000)

        return () => clearTimeout(timeout)
    }, []);

    const getCustomFieldsData = async () => {
        const response: CustomField = await portalService.getClientPortalCustomFields(state.portalId);
        setCustomFields(response);
    }

    const filteredFields = editMode ? customFields.filter(field => field.PerClient === false) : customFields.filter(field => field.PerClient === true);

    if (displayMode === "simple") {
        return (
            <>
                    {filteredFields.map((field, index) => {
                        const value = readPerClientValue(field.Identifier, contactIdentityOf(contact));
                        return (
                            <Col className="col-4 mb-3" key={field.Name + '-custom-field-' + index}>
                                <React.Fragment>
                                    <Form.Label>{field.Name}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={true}
                                        value={value}
                                        placeholder={value || 'Not Submitted'}
                                    />
                                </React.Fragment>
                            </Col>
                        );
                    })}
            </>
        );
    }

    return (
        <Card >
            <Card.Header>
                <Card.Title>Custom Fields</Card.Title>
            </Card.Header>

            <Card.Body>
                <Row >
                    {filteredFields.map((field, index) => {
                        const value = field.Value ?? undefined;
                        return (
                            <Col className="col-4 mb-3" key={field.Name + '-custom-field-' + index}>
                                <React.Fragment>
                                    <Form.Label>{field.Name}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        readOnly={true}
                                        value={value}
                                        placeholder={value || 'Not Submitted'}
                                    />
                                </React.Fragment>
                            </Col>
                        );
                    })}
                </Row>
                {customFields.length === 0 && <Alert>No custom fields added. Please click 'Add Individual Customs Fields' below to get started.</Alert>}
                    <br />
                <Row>
                    <Col>
                        <Button className="me-2" onClick={() => setShowIntakeTemplatesModal(true)}>
                            Add Intake Form from Templates
                        </Button>
                        <Button variant="outline-primary" onClick={() => setShowIndividualFieldsModal(true)}>
                            {customFields.length === 0 ? 'Add' : 'Edit'} Individual Custom Fields
                        </Button>
                        <AddIntakeTemplatesModal
                            show={showIntakeTemplatesModal}
                            onHide={() => {
                                getCustomFieldsData();
                                setShowIntakeTemplatesModal(false);
                            }}
                            onSaved={getCustomFieldsData}
                        />
                        <AddCustomFieldModal
                            getCustomFieldsData={getCustomFieldsData}
                            showModal={showIndividualFieldsModal}
                            onHideModal={() => {
                                getCustomFieldsData();
                                setShowIndividualFieldsModal(false);
                            }}
                        />
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    )
}

export default CustomFields;
